/**
 * Save overlay — the rescue rung of the screenshot ladder.
 *
 * Shown when the native share sheet is unavailable or refused: a client embed
 * whose iframe lacks allow="web-share", Firefox, or a share() that rejected
 * NotAllowedError. Putting the finished image on screen is then the ONLY route
 * to the camera roll — the browser's native long-press callout on an <img>
 * offers Add to Photos (iOS) / Save image (Android), and it needs no user
 * activation at all because the user drives it. The anchor-download rung is not
 * a substitute here: on iOS it lands in Files ▸ Downloads, four taps in another
 * app away from Photos.
 *
 * The image shown is the SAME bytes that were encoded into the File, mark
 * included — never a DOM-overlaid watermark, because proving what is in the file
 * is the one thing a preview is for.
 *
 * No auto-dismiss timer: at this point the card is the user's only path to
 * saving, so it gets unlimited time. It therefore needs exits that a thumb can
 * actually hit, and it has three — a labelled Close button, a tap anywhere on
 * the backdrop, and Escape. The first cut of this rung reused the 20x20
 * .ollo-hints-close glyph, positioned on top of the image: half the iOS 44px
 * minimum, and sitting on the one element whose whole purpose is to swallow long
 * presses, so a slightly-off tap did nothing at all.
 *
 * The backdrop is deliberately modal (it takes pointer events and dims the
 * scene): while the card is up it is the task, and a tap that misses the card is
 * far more likely to mean "get rid of this" than "orbit the scene behind it".
 */

export interface SaveOverlayOptions {
    parent: HTMLElement;
    /**
     * data: URL, not blob:. It costs nothing (the encoder already produced
     * one) and it survives the marketing site's CSP, whose img-src lists
     * 'self' data: but NOT blob: — relevant the day the viewer is inlined into
     * the homepage document instead of iframed.
     */
    src: string;
    /** Translated "touch and hold the image to save it" line. */
    message: string;
    /** Translated label for the Close button. */
    closeLabel: string;
}

export class SaveOverlay {

    private root: HTMLDivElement;

    private backdrop: HTMLDivElement;

    private onKeyDown: (e: KeyboardEvent) => void;

    constructor({ parent, src, message, closeLabel }: SaveOverlayOptions) {
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'ollo-save-backdrop';
        this.backdrop.addEventListener('click', () => this.dispose());
        parent.appendChild(this.backdrop);

        this.root = document.createElement('div');
        this.root.className = 'ollo-save';

        const img = document.createElement('img');
        img.className = 'ollo-save-img';
        img.src = src;
        img.alt = message;
        // .ollo-save-img in viewer.css re-opts into -webkit-touch-callout and
        // user-select: the long-press callout IS this rung, so it must not
        // inherit a `none` from anywhere above.
        this.root.appendChild(img);

        const label = document.createElement('p');
        label.className = 'ollo-save-label';
        label.textContent = message;
        this.root.appendChild(label);

        const close = document.createElement('button');
        close.className = 'ollo-btn ollo-save-close';
        close.type = 'button';
        close.textContent = closeLabel;
        close.setAttribute('aria-label', closeLabel);
        close.addEventListener('click', (e) => { e.stopPropagation(); this.dispose(); });
        this.root.appendChild(close);

        parent.appendChild(this.root);
        // Force a style computation so the hidden state is the transition's
        // from-value. A requestAnimationFrame does NOT do this: rAF callbacks
        // run BEFORE style recalc in the rendering update, so the class would be
        // added before the element was ever computed and the transition would
        // never start. (.ollo-hints gets away with rAF only because it reveals
        // from a setTimeout, a whole task later.)
        void this.root.offsetWidth;
        this.backdrop.classList.add('visible');
        this.root.classList.add('visible');

        this.onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') this.dispose(); };
        window.addEventListener('keydown', this.onKeyDown);
    }

    dispose(): void {
        window.removeEventListener('keydown', this.onKeyDown);
        this.backdrop.remove();
        this.root.remove();
    }
}
